// YouTube URL parser — isolates all YouTube URL parsing logic behind a clean boundary.
// Returns normalized video ID, canonical URL, and optional start time in seconds.

export interface YouTubeParseResult {
  videoId: string;
  canonicalUrl: string;
  startTime?: number;
}

// YouTube video IDs are exactly 11 base64url characters.
const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

/**
 * Parse a t= / start= parameter value into seconds.
 * Handles: "123" (seconds), "1m30s", "1h2m3s", "1h", "1m".
 */
function parseTimestamp(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  // Human-readable format: e.g. 1h2m3s, 1m30s, 30s, 2m
  const hms = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/.exec(trimmed);
  if (hms && trimmed !== '') {
    const h = parseInt(hms[1] || '0', 10);
    const m = parseInt(hms[2] || '0', 10);
    const s = parseInt(hms[3] || '0', 10);
    const total = h * 3600 + m * 60 + s;
    return total > 0 ? total : undefined;
  }

  // Pure integer seconds
  const n = parseInt(trimmed, 10);
  if (!isNaN(n) && n > 0) return n;

  return undefined;
}

function extractTimestamp(params: URLSearchParams): number | undefined {
  // Both t= (standard + youtu.be) and start= (embed) are accepted.
  return parseTimestamp(params.get('t') ?? '') ?? parseTimestamp(params.get('start') ?? '');
}

/**
 * Parse a YouTube URL in any supported format.
 * Throws a user-facing Croop error for invalid or unsupported inputs.
 */
export function parseYouTubeUrl(raw: string): YouTubeParseResult {
  const trimmed = raw.trim();

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    throw new Error('Not a valid URL. Please paste a YouTube link.');
  }

  const { hostname, pathname, searchParams } = url;
  const host = hostname.replace(/^www\./, '').replace(/^m\./, '');

  let videoId: string | null = null;
  let startTime: number | undefined;

  if (host === 'youtu.be') {
    // https://youtu.be/VIDEO_ID[?t=N]
    videoId = pathname.slice(1).split('/')[0] ?? null;
    startTime = extractTimestamp(searchParams);
  } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (pathname.startsWith('/watch')) {
      // https://youtube.com/watch?v=VIDEO_ID[&t=N]
      videoId = searchParams.get('v');
      startTime = extractTimestamp(searchParams);
    } else if (pathname.startsWith('/shorts/')) {
      // https://youtube.com/shorts/VIDEO_ID
      videoId = pathname.split('/')[2] ?? null;
      startTime = extractTimestamp(searchParams);
    } else if (pathname.startsWith('/embed/')) {
      // https://youtube.com/embed/VIDEO_ID[?start=N]
      videoId = pathname.split('/')[2] ?? null;
      startTime = extractTimestamp(searchParams);
    } else if (pathname.startsWith('/live/')) {
      // https://youtube.com/live/VIDEO_ID
      videoId = pathname.split('/')[2] ?? null;
      startTime = extractTimestamp(searchParams);
    } else {
      throw new Error(
        'This YouTube URL format is not supported. Try a /watch, youtu.be, /shorts, or /embed link.'
      );
    }
  } else {
    throw new Error('Only YouTube links are supported. Please paste a youtube.com or youtu.be URL.');
  }

  if (!videoId) {
    throw new Error('Could not find a video ID in this YouTube link.');
  }

  // Strip any trailing query garbage from the ID (e.g. if split left a '?…')
  videoId = videoId.split('?')[0].split('&')[0];

  if (!VIDEO_ID_RE.test(videoId)) {
    throw new Error(
      `The video ID "${videoId}" looks invalid. Check that the link points to a real YouTube video.`
    );
  }

  const canonicalUrl = `https://www.youtube.com/watch?v=${videoId}`;

  return { videoId, canonicalUrl, startTime };
}
